/**
 * Bilingual glossary of Quran-study terms rendered at /glossary.
 *
 * Every term a student meets in a lesson, on a teacher profile or in one of
 * the blog articles should be definable from this one page. Definitions must
 * stay factual and uncontroversial: where scholars differ, say so rather than
 * picking a side. `link` points at the article or landing page that treats the
 * term in depth, so the glossary doubles as an internal link hub.
 */

export type GlossaryCategory = 'tajweed' | 'recitation' | 'hifz' | 'transmission' | 'study';

export interface GlossaryTerm {
  /** Anchor id, also the transliteration slug. */
  id: string;
  category: GlossaryCategory;
  /** Transliterated name (EN column) and the Arabic term. */
  en: string;
  ar: string;
  defEn: string;
  defAr: string;
  link?: string;
}

export const GLOSSARY_CATEGORIES: { id: GlossaryCategory; en: string; ar: string }[] = [
  { id: 'tajweed', en: 'Tajweed and pronunciation', ar: 'التجويد والنطق' },
  { id: 'recitation', en: 'Recitation and reading', ar: 'التلاوة والقراءة' },
  { id: 'hifz', en: 'Memorisation', ar: 'الحفظ' },
  { id: 'transmission', en: 'Transmission and Ijazah', ar: 'الرواية والإجازة' },
  { id: 'study', en: 'Study and people', ar: 'الدراسة والأعلام' },
];

export const GLOSSARY: GlossaryTerm[] = [
  // ── Tajweed and pronunciation ────────────────────────────────────────────
  {
    id: 'tajweed', category: 'tajweed', en: 'Tajweed', ar: 'التجويد',
    defEn: 'The science of reciting the Quran as it was transmitted: giving every letter its correct articulation point, its attributes, and its due measure of length.',
    defAr: 'علم أداء القرآن كما نُقل: إعطاء كل حرف مخرجه وصفاته ومقداره من المد وغيره.',
    link: '/tajweed-classes',
  },
  {
    id: 'makhraj', category: 'tajweed', en: 'Makhraj', ar: 'المخرج',
    defEn: 'The place in the mouth, throat or nasal cavity from which a letter is produced. There are 17 detailed articulation points in five general areas.',
    defAr: 'موضع خروج الحرف من الفم أو الحلق أو الخيشوم، والمخارج التفصيلية سبعة عشر في خمسة مخارج عامة.',
    link: '/blog/makharij-al-huruf-guide',
  },
  {
    id: 'sifah', category: 'tajweed', en: 'Sifah', ar: 'الصفة',
    defEn: 'An attribute of a letter — the manner in which it is produced. Letters that share a makhraj, such as ta, dal and ta, are distinguished only by their attributes.',
    defAr: 'كيفية نطق الحرف، وبها يفترق ما اشترك في المخرج كالتاء والدال والطاء.',
    link: '/blog/sifat-al-huruf-guide',
  },
  {
    id: 'ghunnah', category: 'tajweed', en: 'Ghunnah', ar: 'الغنة',
    defEn: 'The nasal sound inherent in noon and meem, held for about two counts wherever the rules require it.',
    defAr: 'صوت يخرج من الخيشوم يلازم النون والميم، ويُمَدّ بمقدار حركتين حيث اقتضته الأحكام.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'idgham', category: 'tajweed', en: 'Idgham', ar: 'الإدغام',
    defEn: 'Merging a noon saakinah or tanween into a following letter of yarmaloon, with or without ghunnah depending on the letter.',
    defAr: 'إدخال النون الساكنة أو التنوين في حرف من حروف (يرملون) بعدها، بغنة أو بغير غنة بحسب الحرف.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'ikhfa', category: 'tajweed', en: 'Ikhfa', ar: 'الإخفاء',
    defEn: 'Concealing a noon saakinah or tanween between clear pronunciation and merging, with ghunnah, before fifteen letters.',
    defAr: 'النطق بالنون الساكنة أو التنوين بحالة بين الإظهار والإدغام مع الغنة عند خمسة عشر حرفًا.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'izhar', category: 'tajweed', en: 'Izhar', ar: 'الإظهار',
    defEn: 'Pronouncing a noon saakinah or tanween clearly, without ghunnah added, before the six throat letters.',
    defAr: 'إخراج النون الساكنة أو التنوين من مخرجها من غير غنة زائدة عند حروف الحلق الستة.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'iqlab', category: 'tajweed', en: 'Iqlab', ar: 'الإقلاب',
    defEn: 'Turning a noon saakinah or tanween into a meem with ghunnah when a ba follows.',
    defAr: 'قلب النون الساكنة أو التنوين ميمًا مع الغنة إذا جاء بعدها باء.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'qalqalah', category: 'tajweed', en: 'Qalqalah', ar: 'القلقلة',
    defEn: 'The echo or bounce heard on the letters qaf, ta, ba, jeem and dal when they carry a sukoon. The bounce carries no vowel colour.',
    defAr: 'اضطراب الصوت عند النطق بحروف (قطب جد) ساكنة، من غير أن يُشرب الصوت لون حركة.',
    link: '/blog/sifat-al-huruf-guide',
  },
  {
    id: 'madd', category: 'tajweed', en: 'Madd', ar: 'المد',
    defEn: 'Lengthening the sound of a madd letter — alif, waw or ya with sukoon preceded by its matching vowel — for a measured number of counts.',
    defAr: 'إطالة الصوت بحرف من حروف المد (الألف والواو والياء) بمقدار معلوم من الحركات.',
    link: '/blog/tajweed-rules-101',
  },
  {
    id: 'madd-tabii', category: 'tajweed', en: 'Madd Tabee\'i', ar: 'المد الطبيعي',
    defEn: 'The natural madd of two counts, with no hamzah or sukoon after the madd letter. It is the baseline every other madd is measured against.',
    defAr: 'المد الأصلي بمقدار حركتين، ولا يأتي بعد حرفه همز ولا سكون، وعليه تُقاس بقية المدود.',
  },
  {
    id: 'tafkheem', category: 'tajweed', en: 'Tafkheem', ar: 'التفخيم',
    defEn: 'Pronouncing a letter heavily, with the back of the tongue raised. The seven letters of isti\'la are always heavy; the ra and the lam of the name of Allah vary by context.',
    defAr: 'تسمين الحرف برفع أقصى اللسان. وحروف الاستعلاء السبعة مفخمة دائمًا، والراء ولام لفظ الجلالة بحسب السياق.',
    link: '/blog/sifat-al-huruf-guide',
  },
  {
    id: 'tarqeeq', category: 'tajweed', en: 'Tarqeeq', ar: 'الترقيق',
    defEn: 'Pronouncing a letter lightly, without raising the back of the tongue — the opposite of tafkheem.',
    defAr: 'إنحاف الحرف من غير رفع لأقصى اللسان، وهو ضد التفخيم.',
  },
  {
    id: 'sukoon', category: 'tajweed', en: 'Sukoon', ar: 'السكون',
    defEn: 'The mark showing that a letter carries no vowel and closes the syllable.',
    defAr: 'علامة تدل على خلو الحرف من الحركة، فيُغلق بها المقطع.',
    link: '/blog/arabic-alphabet-for-beginners',
  },
  {
    id: 'shaddah', category: 'tajweed', en: 'Shaddah', ar: 'الشدة',
    defEn: 'The mark showing a doubled letter: the first occurrence is silent and the second carries the vowel. Rushing it changes meanings.',
    defAr: 'علامة تضعيف الحرف: أوله ساكن وثانيه متحرك، والعجلة فيها تُغيّر المعنى.',
    link: '/blog/arabic-alphabet-for-beginners',
  },
  {
    id: 'tanween', category: 'tajweed', en: 'Tanween', ar: 'التنوين',
    defEn: 'A doubled vowel mark at the end of a noun, pronounced as an extra noon sound that is heard but not written.',
    defAr: 'نون ساكنة زائدة تلحق آخر الاسم لفظًا لا خطًّا، وتُرسم بتكرار الحركة.',
    link: '/blog/arabic-alphabet-for-beginners',
  },
  {
    id: 'lahn-jali', category: 'tajweed', en: 'Lahn Jali', ar: 'اللحن الجلي',
    defEn: 'A clear error that changes a letter, a vowel or a word — for example reading one letter as another. Scholars regard avoiding it as obligatory.',
    defAr: 'خطأ ظاهر يُخلّ بالحرف أو الحركة أو الكلمة، كإبدال حرف بحرف، واجتنابه واجب عند العلماء.',
    link: '/blog/common-tajweed-mistakes',
  },
  {
    id: 'lahn-khafi', category: 'tajweed', en: 'Lahn Khafi', ar: 'اللحن الخفي',
    defEn: 'A hidden error that does not change the word but breaks a rule of recitation, such as shortening a madd or dropping a ghunnah.',
    defAr: 'خطأ لا يُخلّ بالكلمة لكنه يُخالف أحكام الأداء، كقصر المد أو ترك الغنة.',
    link: '/blog/common-tajweed-mistakes',
  },
  {
    id: 'waqf', category: 'tajweed', en: 'Waqf', ar: 'الوقف',
    defEn: 'Stopping at the end of a word with the intention of continuing. Where you may stop is governed by meaning, and the mushaf marks it with small letters.',
    defAr: 'قطع الصوت على آخر الكلمة مع نية الاستئناف، وحكمه راجع إلى المعنى، وله علامات في المصحف.',
    link: '/blog/waqf-and-ibtida-signs',
  },
  {
    id: 'ibtida', category: 'tajweed', en: 'Ibtida', ar: 'الابتداء',
    defEn: 'Beginning again after a stop. It is always a choice, so the scholars require that the starting point leave the meaning sound.',
    defAr: 'الشروع في القراءة بعد الوقف، وهو اختياري دائمًا، فاشترطوا أن يستقيم به المعنى.',
    link: '/blog/waqf-and-ibtida-signs',
  },
  {
    id: 'saktah', category: 'tajweed', en: 'Saktah', ar: 'السكت',
    defEn: 'A brief pause of about two counts with the voice cut and no breath taken. Hafs from Asim has four such places.',
    defAr: 'وقفة يسيرة بمقدار حركتين من غير تنفس، وهي في حفص عن عاصم في أربعة مواضع.',
    link: '/blog/waqf-and-ibtida-signs',
  },

  // ── Recitation and reading ───────────────────────────────────────────────
  {
    id: 'tilawah', category: 'recitation', en: 'Tilawah', ar: 'التلاوة',
    defEn: 'Reciting the Quran aloud, following the text as it was transmitted, with the attention its meaning deserves.',
    defAr: 'قراءة القرآن على وجهه المنقول مع إقبال القلب على معناه.',
    link: '/blog/adab-of-quran-recitation',
  },
  {
    id: 'tarteel', category: 'recitation', en: 'Tarteel', ar: 'الترتيل',
    defEn: 'Unhurried, clear recitation, letter by letter — the manner commanded in Surah Al-Muzzammil and the pace at which reflection is possible.',
    defAr: 'القراءة على تمهّل وبيان حرفًا حرفًا، وهو المأمور به في سورة المزمل، وبه يتهيأ التدبر.',
    link: '/blog/how-to-improve-quran-recitation',
  },
  {
    id: 'tahqiq', category: 'recitation', en: 'Tahqiq', ar: 'التحقيق',
    defEn: 'The slowest recognised pace of recitation, used for teaching and practice because it gives every rule its full measure.',
    defAr: 'أبطأ مراتب الأداء، ويُستعمل في التعليم والتدريب لأنه يُوفي كل حكم حقه.',
  },
  {
    id: 'tadwir', category: 'recitation', en: 'Tadwir', ar: 'التدوير',
    defEn: 'The moderate pace of recitation, between tahqiq and hadr.',
    defAr: 'مرتبة متوسطة في الأداء بين التحقيق والحدر.',
  },
  {
    id: 'hadr', category: 'recitation', en: 'Hadr', ar: 'الحدر',
    defEn: 'Fast recitation, valid only when every rule is still observed. It is for those who have mastered the rules, not for learners.',
    defAr: 'الإسراع في القراءة مع مراعاة الأحكام، وهو لمن أتقنها لا للمتعلم.',
  },
  {
    id: 'talaqqi', category: 'recitation', en: 'Talaqqi', ar: 'التلقي',
    defEn: 'Learning the Quran by reciting to a teacher and being corrected, face to face, rather than from books or recordings alone.',
    defAr: 'أخذ القرآن بالقراءة على شيخ يصحح، مشافهةً، لا من الكتب والتسجيلات وحدها.',
  },
  {
    id: 'khatmah', category: 'recitation', en: 'Khatmah', ar: 'الختمة',
    defEn: 'A complete reading of the Quran from Al-Fatihah to An-Nas. In the standard mushaf that is 604 pages, or about 20 pages a day across a month.',
    defAr: 'قراءة القرآن كاملًا من الفاتحة إلى الناس، وهي في المصحف المعتاد 604 صفحات، نحو عشرين صفحة يوميًا في الشهر.',
    link: '/blog/ramadan-quran-plan',
  },
  {
    id: 'sajdah-tilawah', category: 'recitation', en: 'Sajdah at-Tilawah', ar: 'سجدة التلاوة',
    defEn: 'A single prostration made on reciting or hearing one of the marked verses of sajdah. The schools differ over whether it is obligatory or recommended.',
    defAr: 'سجدة واحدة عند تلاوة إحدى آيات السجدة أو سماعها، واختلف الفقهاء في وجوبها واستحبابها.',
    link: '/blog/adab-of-quran-recitation',
  },
  {
    id: 'istiadhah', category: 'recitation', en: 'Isti\'adhah', ar: 'الاستعاذة',
    defEn: 'Saying "a\'udhu billahi min ash-shaytan ir-rajim" before reciting, as commanded in Surah An-Nahl.',
    defAr: 'قول "أعوذ بالله من الشيطان الرجيم" قبل التلاوة، كما أُمر في سورة النحل.',
    link: '/blog/adab-of-quran-recitation',
  },
  {
    id: 'basmalah', category: 'recitation', en: 'Basmalah', ar: 'البسملة',
    defEn: 'Saying "bismillah ir-rahman ir-rahim" at the beginning of a surah. Surah At-Tawbah is the exception.',
    defAr: 'قول "بسم الله الرحمن الرحيم" عند ابتداء السورة، وتُستثنى سورة التوبة.',
    link: '/blog/adab-of-quran-recitation',
  },

  // ── Memorisation ─────────────────────────────────────────────────────────
  {
    id: 'hifz', category: 'hifz', en: 'Hifz', ar: 'الحفظ',
    defEn: 'Memorising the Quran, whether a portion of it or the whole text, and holding it by regular revision.',
    defAr: 'استظهار القرآن أو بعضه عن ظهر قلب، وحفظه بالمراجعة المنتظمة.',
    link: '/hifz-program',
  },
  {
    id: 'hafiz', category: 'hifz', en: 'Hafiz', ar: 'الحافظ',
    defEn: 'Someone who has memorised the whole Quran and can recite it from memory. The feminine form is hafizah.',
    defAr: 'من استظهر القرآن كله وقدر على سرده من حفظه، ومؤنثه حافظة.',
    link: '/blog/virtues-of-memorizing-quran',
  },
  {
    id: 'murajaah', category: 'hifz', en: 'Muraja\'ah', ar: 'المراجعة',
    defEn: 'Revision of memorised portions. Without a fixed daily rotation, memorisation fades within weeks regardless of how well it was learned.',
    defAr: 'إعادة المحفوظ وتثبيته، وبغير ورد يومي منتظم يتفلّت المحفوظ في أسابيع مهما أُتقن.',
    link: '/blog/hifz-revision-schedule',
  },
  {
    id: 'sabaq', category: 'hifz', en: 'Sabaq', ar: 'السبق',
    defEn: 'The new portion memorised today — a term widely used in hifz schools of the Indian subcontinent and beyond.',
    defAr: 'الورد الجديد الذي يُحفظ اليوم، ومصطلحه شائع في مدارس التحفيظ.',
  },
  {
    id: 'sabqi', category: 'hifz', en: 'Sabqi', ar: 'السبقي',
    defEn: 'Recent revision: what was memorised over the last days or weeks, recited daily so it settles.',
    defAr: 'المراجعة القريبة: ما حُفظ في الأيام أو الأسابيع الماضية، يُسرد يوميًا حتى يثبت.',
  },
  {
    id: 'manzil', category: 'hifz', en: 'Manzil', ar: 'المنزل',
    defEn: 'Distant revision: older memorised portions recited on a rotation, and also the name of each of the seven traditional divisions of the Quran.',
    defAr: 'المراجعة البعيدة من المحفوظ القديم على دورة، ويُطلق أيضًا على أحد أقسام القرآن السبعة.',
  },
  {
    id: 'juz', category: 'hifz', en: 'Juz', ar: 'الجزء',
    defEn: 'One of the thirty parts the Quran is divided into for reading and memorisation, roughly twenty pages of the standard mushaf.',
    defAr: 'أحد أجزاء القرآن الثلاثين المقسَّمة للقراءة والحفظ، ونحو عشرين صفحة من المصحف المعتاد.',
    link: '/blog/juz-amma-memorization-guide',
  },
  {
    id: 'hizb', category: 'hifz', en: 'Hizb', ar: 'الحزب',
    defEn: 'Half a juz. The Quran contains sixty ahzab, each further divided into four quarters (arba\').',
    defAr: 'نصف الجزء، والقرآن ستون حزبًا، وكل حزب أربعة أرباع.',
  },
  {
    id: 'mushaf', category: 'hifz', en: 'Mushaf', ar: 'المصحف',
    defEn: 'The written copy of the Quran. Memorising from one consistent printing matters, because the page layout becomes part of visual memory.',
    defAr: 'الكتاب المكتوب فيه القرآن، ويُنصح بلزوم طبعة واحدة في الحفظ لأن صورة الصفحة جزء من الذاكرة البصرية.',
  },

  // ── Transmission and Ijazah ──────────────────────────────────────────────
  {
    id: 'ijazah', category: 'transmission', en: 'Ijazah', ar: 'الإجازة',
    defEn: 'A teacher\'s authorisation, granted after a student recites the whole Quran to them correctly, permitting the student to recite and teach it as received.',
    defAr: 'إذن الشيخ لتلميذه بعد أن يتلو عليه القرآن كاملًا متقنًا، بأن يُقرئ ويروي كما تلقى.',
    link: '/ijazah',
  },
  {
    id: 'sanad', category: 'transmission', en: 'Sanad', ar: 'السند',
    defEn: 'The chain of teachers through which a recitation is transmitted, each having recited to the one before, back to the Prophet, peace be upon him.',
    defAr: 'سلسلة الشيوخ التي تُنقل بها القراءة، كل واحد قرأ على من قبله إلى النبي صلى الله عليه وسلم.',
    link: '/blog/what-is-ijazah',
  },
  {
    id: 'mutawatir', category: 'transmission', en: 'Mutawatir', ar: 'المتواتر',
    defEn: 'Transmitted by so many narrators at every level that error or collusion is inconceivable. The ten canonical readings are transmitted this way.',
    defAr: 'ما نقله جمع يُحيل العادة تواطؤهم على الكذب في كل طبقة، وبه نُقلت القراءات العشر.',
    link: '/blog/ten-qiraat-explained',
  },
  {
    id: 'qiraah', category: 'transmission', en: 'Qira\'ah', ar: 'القراءة',
    defEn: 'A complete reading of the Quran transmitted from one of the imams of recitation. Ten readings meet the conditions of acceptance.',
    defAr: 'وجه كامل في الأداء منقول عن إمام من أئمة القراءة، والقراءات المستوفية للشروط عشر.',
    link: '/blog/ten-qiraat-explained',
  },
  {
    id: 'riwayah', category: 'transmission', en: 'Riwayah', ar: 'الرواية',
    defEn: 'One student\'s transmission of an imam\'s reading. Each of the ten imams has two principal narrators, giving twenty riwayat.',
    defAr: 'ما نقله أحد تلاميذ الإمام عن قراءته، ولكل من الأئمة العشرة راويان، فهي عشرون رواية.',
    link: '/blog/hafs-vs-warsh',
  },
  {
    id: 'hafs', category: 'transmission', en: 'Hafs an Asim', ar: 'حفص عن عاصم',
    defEn: 'The narration of Hafs from the reading of Asim of Kufa — the most widely recited transmission in the world today and the default on this platform.',
    defAr: 'رواية حفص عن قراءة عاصم الكوفي، وهي أوسع الروايات انتشارًا اليوم وهي المعتمدة في المنصة.',
    link: '/blog/hafs-vs-warsh',
  },
  {
    id: 'warsh', category: 'transmission', en: 'Warsh an Nafi', ar: 'ورش عن نافع',
    defEn: 'The narration of Warsh from the reading of Nafi of Madinah, recited across Morocco, Algeria and much of West Africa.',
    defAr: 'رواية ورش عن قراءة نافع المدني، وعليها القراءة في المغرب والجزائر وكثير من غرب أفريقيا.',
    link: '/blog/hafs-vs-warsh',
  },
  {
    id: 'shatibiyyah', category: 'transmission', en: 'Shatibiyyah', ar: 'الشاطبية',
    defEn: 'The didactic poem of Imam ash-Shatibi setting out the seven readings. Students of the qiraat memorise it before reciting the ten.',
    defAr: 'منظومة الإمام الشاطبي في القراءات السبع، يحفظها طالب القراءات قبل قراءة العشر.',
    link: '/blog/ten-qiraat-explained',
  },
  {
    id: 'ahruf', category: 'transmission', en: 'Ahruf', ar: 'الأحرف',
    defEn: 'The seven ahruf mentioned in the hadith in which the Quran was revealed. They are not the same as the seven qiraat, and scholars differ over their exact nature.',
    defAr: 'الأحرف السبعة التي نزل بها القرآن كما في الحديث، وليست هي القراءات السبع، وقد اختلف العلماء في تحقيق معناها.',
    link: '/blog/ten-qiraat-explained',
  },

  // ── Study and people ─────────────────────────────────────────────────────
  {
    id: 'noorani-qaida', category: 'study', en: 'Noorani Qaida', ar: 'القاعدة النورانية',
    defEn: 'The classical beginner primer that teaches the letters, their shapes, the vowel marks and joining, in the order that gets a student reading the mushaf fastest.',
    defAr: 'المتن التعليمي للمبتدئين في الحروف وأشكالها والحركات والوصل، بترتيب يُوصل إلى قراءة المصحف أسرع.',
    link: '/noorani-qaida',
  },
  {
    id: 'tafsir', category: 'study', en: 'Tafsir', ar: 'التفسير',
    defEn: 'Explanation of the meanings of the Quran, drawing on the Quran itself, the sunnah, the reports of the companions and the Arabic language.',
    defAr: 'بيان معاني القرآن بالقرآن والسنة وأقوال الصحابة ولغة العرب.',
  },
  {
    id: 'fusha', category: 'study', en: 'Fusha', ar: 'الفصحى',
    defEn: 'Classical and standard literary Arabic — the register of the Quran, distinct from the spoken dialects.',
    defAr: 'العربية الفصيحة، وهي لغة القرآن، بخلاف اللهجات المحكية.',
    link: '/quranic-arabic',
  },
  {
    id: 'irab', category: 'study', en: 'I\'rab', ar: 'الإعراب',
    defEn: 'The grammatical analysis of Arabic words and the case endings that follow from it — the key to reading a verse\'s structure rather than guessing at it.',
    defAr: 'بيان مواقع الكلمات من الإعراب وحركات أواخرها، وبه يُفهم تركيب الآية.',
    link: '/quranic-arabic',
  },
  {
    id: 'halaqah', category: 'study', en: 'Halaqah', ar: 'الحلقة',
    defEn: 'A study circle in which students recite to a teacher in turn. Online, its equivalent is the small group session.',
    defAr: 'مجلس يقرأ فيه الطلاب على الشيخ بالتناوب، ويقابلها في التعليم عن بعد الجلسة الجماعية الصغيرة.',
    link: '/group-sessions',
  },
  {
    id: 'qari', category: 'study', en: 'Qari', ar: 'القارئ',
    defEn: 'A reciter; in the technical sense, one who has mastered a reading and recites it with a connected chain. The feminine is qari\'ah.',
    defAr: 'المتقن للقراءة الراوي لها بسند متصل، ومؤنثه قارئة.',
  },
  {
    id: 'mujiz', category: 'study', en: 'Mujiz and Mujaz', ar: 'المُجيز والمُجاز',
    defEn: 'The mujiz is the teacher who grants an Ijazah; the mujaz is the student who receives it.',
    defAr: 'المُجيز هو الشيخ الذي يمنح الإجازة، والمُجاز هو الطالب الذي يأخذها.',
    link: '/blog/how-to-get-ijazah-online',
  },
];
